import React, { FC, useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@material-ui/core';
import { ADEQUACY_OPTIONS } from './adequacy_options';

interface DetailFormValues {
    description: string;
    adequacy_id: number | '';
}

interface AssessmentTemplateDetailDialogProps {
    open: boolean;
    initialValues: DetailFormValues;
    isEditing: boolean;
    onSubmit: (values: DetailFormValues) => void;
    onClose: () => void;
    disabledAdequacyIds?: number[];
}

const defaultValues: DetailFormValues = {
    description: '',
    adequacy_id: ''
};

const AssessmentTemplateDetailDialog: FC<AssessmentTemplateDetailDialogProps> = ({
    open,
    initialValues,
    isEditing,
    onSubmit,
    onClose,
    disabledAdequacyIds = []
}) => {
    const [formValues, setFormValues] = useState<DetailFormValues>(initialValues || defaultValues);

    useEffect(() => {
        if (open) {
            setFormValues(initialValues || defaultValues);
        }
    }, [initialValues, open]);

    const handleChange = (key: keyof DetailFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues(prev => ({ ...prev, [key]: event.target.value }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!formValues.description.trim() || !formValues.adequacy_id) {
            return;
        }
        onSubmit({
            description: formValues.description.trim(),
            adequacy_id: Number(formValues.adequacy_id)
        });
    };

    return (
        <Dialog open={open} onClose={onClose} aria-labelledby="detail-dialog-title" fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle id="detail-dialog-title">{isEditing ? 'Edit detail' : 'Add detail'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Description"
                        value={formValues.description}
                        onChange={handleChange('description')}
                        margin="dense"
                        fullWidth
                        multiline
                        rows={3}
                    />
                    <FormControl margin="dense" fullWidth required>
                        <InputLabel id="detail-adequacy-label">Adequacy</InputLabel>
                        <Select
                            labelId="detail-adequacy-label"
                            value={formValues.adequacy_id}
                            onChange={(event) => handleChange('adequacy_id')(event as any)}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Select adequacy</em>
                            </MenuItem>
                            {ADEQUACY_OPTIONS.map(option => (
                                <MenuItem value={option.id} key={option.id} disabled={disabledAdequacyIds.includes(option.id)}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="primary">
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" variant="contained">
                        {isEditing ? 'Save' : 'Create'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export type { DetailFormValues };
export default AssessmentTemplateDetailDialog;
